import { useState } from 'react';
import Modal from '../../components/Modal';
import { adminApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import PasswordInput from '../../components/PasswordInput';
import { docsCount } from '../../utils/recipientAlias';
import Icon from '../../components/Icon';

export { ROLE_COLORS, ROLE_LABELS } from '../../utils/roleDisplay';

// The three create/edit modals. They lived on the old AdminPage; they were
// moved here (rather than copied) in Cut 1 precisely so the two screens could
// not drift while both existed. AdminPage is now deleted and these are the
// only copies.
//
// They write through adminApi on purpose: /api/admin/* stays the single writer
// for users, restaurants and networks. The Command Center adds read surfaces
// and one scoped action (resend invite); it does not fork the write path.

// Must stay in sync with VALID_DOC_TYPES in server/lib/restaurantSettings.js.
export const DOKKA_DOC_TYPES = [
  'חשבונית', 'חשבונית זיכוי', 'חשבונית מרכזת',
  'תעודת משלוח', 'תעודת החזרה',
  'חשבונית/קבלה', 'קבלה', 'חשבון עסקה', 'כרטסת', 'אחר',
];

export function UserModal({ user, restaurants, onSave, onClose }) {
  // Server returns `restaurant_ids: []` for viewers (from user_restaurants).
  // For older payloads that only had `restaurant_id`, fall back to that single id.
  const initialIds = Array.isArray(user?.restaurant_ids) && user.restaurant_ids.length > 0
    ? user.restaurant_ids.map(String)
    : (user?.restaurant_id ? [String(user.restaurant_id)] : []);
  const [form, setForm] = useState({
    email: user?.email || '',
    password: '',
    role: user?.role || 'viewer',
    restaurant_ids: initialIds,
    can_edit_payment_terms: user?.can_edit_payment_terms === 1,
    can_view_discounts: user?.can_view_discounts === 1,
  });
  const [saving, setSaving] = useState(false);

  const toggleRestaurant = (id) => {
    const s = String(id);
    setForm(f => ({
      ...f,
      restaurant_ids: f.restaurant_ids.includes(s)
        ? f.restaurant_ids.filter(x => x !== s)
        : [...f.restaurant_ids, s],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.role !== 'admin' && form.restaurant_ids.length === 0) {
      toast('יש לבחור לפחות מסעדה אחת', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = {
        email: form.email,
        password: form.password,
        role: form.role,
        restaurant_ids: form.role === 'admin' ? [] : form.restaurant_ids.map(Number),
      };
      if (user) {
        if (!data.password) delete data.password;
        await adminApi.updateUser(user.id, data);
        toast('המשתמש עודכן');
      } else {
        // Empty password on create = server sends a welcome email with a
        // setup link. Don't send the empty string — let the server's
        // `willSendInvite = !password` branch trigger.
        if (!data.password) delete data.password;
        const r = await adminApi.createUser(data);
        if (r.data?.invite_error) {
          toast(r.data.invite_error, 'error');
        } else if (r.data?.invite_sent) {
          toast('המשתמש נוצר ונשלח מייל להגדרת סיסמה');
        } else {
          toast('המשתמש נוצר');
        }
      }
      onSave();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size={460}
      closeOnBackdrop={false}
      ariaLabel={user ? 'עריכת משתמש' : 'משתמש חדש'}
    >
      <h2 className="modal-title">{user ? 'עריכת משתמש' : 'משתמש חדש'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>אימייל *</label>
          <input className="form-control" type="email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
        </div>
        <div className="form-group">
          <label>
            {user
              ? 'סיסמה חדשה (השאר ריק לשמור הקיימת)'
              : 'סיסמה (אופציונלי — השאר ריק לשליחת מייל הרשמה)'}
          </label>
          <PasswordInput className="form-control" value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder={user ? '••••••••' : ''} />
        </div>
        <div className="form-group">
          <label>תפקיד</label>
          <select className="form-control" value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="viewer">צופה</option>
            <option value="manager">מנהל</option>
            <option value="hq_admin">בעלים / מטה</option>
            <option value="admin">אדמין</option>
          </select>
          <div style={{ color: '#888', fontSize: '0.78rem', marginTop: 4 }}>
            צופה: קריאה בלבד · מנהל: זיכויים, סיווגים ותנאי תשלום · בעלים / מטה:
            {' '}כמו מנהל, ורואים גם ספקים מוסתרים · אדמין: הכל.
          </div>
        </div>
        {form.role !== 'admin' && (
          <div className="form-group">
            <label>מסעדות * (אפשר לבחור יותר מאחת)</label>
            <div style={{
              border: '1px solid #d4dae5', borderRadius: 6, padding: '8px 10px',
              maxHeight: 180, overflowY: 'auto', background: '#fafbfc',
            }}>
              {restaurants.length === 0 ? (
                <div style={{ color: '#888', fontSize: '0.85rem' }}>אין מסעדות</div>
              ) : (
                restaurants.map(r => {
                  const checked = form.restaurant_ids.includes(String(r.id));
                  return (
                    <label key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '4px 0', cursor: 'pointer',
                    }}>
                      <input type="checkbox" checked={checked}
                        onChange={() => toggleRestaurant(r.id)} />
                      <span>{r.name}</span>
                    </label>
                  );
                })
              )}
            </div>
            <div style={{ color: '#888', fontSize: '0.78rem', marginTop: 4 }}>
              הראשונה שתבחר/י תהיה ברירת המחדל אחרי התחברות.
            </div>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Dual-purpose: create when `restaurant` is null, edit otherwise.
export function RestaurantModal({ restaurant, onSave, onClose }) {
  const editing = !!restaurant;
  const [name, setName] = useState(restaurant?.name || '');
  const [businessId, setBusinessId] = useState(restaurant?.business_id || '');
  // Alternative names suppliers print instead of the legal name. Stored as a
  // JSON string on the row; parsed defensively (older rows have NULL).
  const [aliases, setAliases] = useState(() => {
    try { const v = JSON.parse(restaurant?.name_aliases || '[]'); return Array.isArray(v) ? v : []; }
    catch { return []; }
  });
  const [aliasDraft, setAliasDraft] = useState('');
  // ח.פ. numbers taught from the warning chip as "the supplier's, not ours".
  // Only removable here — adding happens from a document.
  const [ignoredBids, setIgnoredBids] = useState(() => {
    try { const v = JSON.parse(restaurant?.ignored_recipient_bids || '[]'); return Array.isArray(v) ? v : []; }
    catch { return []; }
  });
  const addAlias = () => {
    const v = aliasDraft.replace(/\s+/g, ' ').trim();
    if (v && !aliases.includes(v)) setAliases([...aliases, v]);
    setAliasDraft('');
  };
  const [isDemo, setIsDemo] = useState(!!restaurant?.is_demo);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  // ח.פ. is optional, but if filled it must be exactly 9 digits. Used as a
  // second validation signal when matching uploaded supplier documents to the
  // restaurant.
  const businessIdError = businessId.length > 0 && !/^\d{9}$/.test(businessId)
    ? 'ח.פ. חייב להיות 9 ספרות'
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (businessIdError) return;
    setSaving(true);
    try {
      const pendingAlias = aliasDraft.replace(/\s+/g, ' ').trim();
      const payload = {
        name: name.trim(), business_id: businessId.trim(), is_demo: isDemo,
        // A typed-but-not-Entered alias is kept rather than silently dropped.
        name_aliases: pendingAlias && !aliases.includes(pendingAlias) ? [...aliases, pendingAlias] : aliases,
        ...(editing ? { ignored_recipient_bids: ignoredBids } : {}),
      };
      if (editing) {
        // Echo back the row version we loaded. If another admin saved in the
        // meantime the server answers 409 and nobody's edit is silently lost.
        payload.expected_updated_at = restaurant.updated_at;
        const { data } = await adminApi.updateRestaurant(restaurant.id, payload);
        toast(data?.recomputed
          ? `המסעדה עודכנה · ${docsCount(data.recomputed)} כבר לא ${data.recomputed === 1 ? 'מסומן' : 'מסומנים'} כלא תואם`
          : 'המסעדה עודכנה');
      } else {
        await adminApi.createRestaurant(payload);
        toast('המסעדה נוצרה');
      }
      onSave();
    } catch (err) {
      // 409 = somebody else saved first. Keep the dialog OPEN with the typed
      // text intact so the edit isn't lost while they go and look.
      const msg = err.response?.data?.error || 'שגיאה';
      toast(msg, 'error');
      if (err.response?.status === 409) setConflict(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size={380}
      closeOnBackdrop={false}
      ariaLabel={editing ? 'עריכת מסעדה' : 'מסעדה חדשה'}
    >
      <h2 className="modal-title">{editing ? 'עריכת מסעדה' : 'מסעדה חדשה'}</h2>
      {conflict && (
        <div className="cc-error" style={{ marginBottom: 12 }}>{conflict}</div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>שם המסעדה *</label>
          <input className="form-control" value={name} autoFocus
            onChange={e => setName(e.target.value)} required placeholder="שם המסעדה" />
        </div>
        <div className="form-group">
          <label>ח.פ. (אופציונלי)</label>
          <input
            className="form-control"
            value={businessId}
            inputMode="numeric"
            maxLength={9}
            onChange={e => setBusinessId(e.target.value.replace(/\D/g, ''))}
            placeholder="9 ספרות"
          />
          {businessIdError && (
            <div style={{ color: '#b91c1c', fontSize: '0.8rem', marginTop: 4 }}>{businessIdError}</div>
          )}
        </div>
        {editing && (
          <div className="form-group">
            <label>שמות חלופיים במסמכים</label>
            {aliases.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {aliases.map(a => (
                  <span key={a} className="cc-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {a}
                    <button type="button" aria-label={`הסר ${a}`}
                      onClick={() => setAliases(aliases.filter(x => x !== a))}
                      style={{ border: 0, background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <input
              className="form-control"
              value={aliasDraft}
              onChange={e => setAliasDraft(e.target.value)}
              // Enter would otherwise submit the surrounding form.
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
              onBlur={addAlias}
              placeholder="הקלד שם ולחץ Enter"
            />
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 4 }}>
              שמות שספקים כותבים על המסמך במקום השם הרשמי. משמשים לזיהוי המסעדה כשאין ח.פ. במסמך.
            </div>
          </div>
        )}
        {editing && ignoredBids.length > 0 && (
          <div className="form-group">
            <label>ח.פ. של ספקים (להתעלם בבדיקת הנמען)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ignoredBids.map(b => (
                <span key={b} className="cc-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {b}
                  <button type="button" aria-label={`הסר ${b}`}
                    onClick={() => setIgnoredBids(ignoredBids.filter(x => x !== b))}
                    style={{ border: 0, background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
                </span>
              ))}
            </div>
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 4 }}>
              מספרים שסומנו במסמך כ"ח.פ. של הספק". כשהם נקראים כח.פ. הנמען — הבדיקה נעשית לפי השם.
            </div>
          </div>
        )}
        {/* Not a <label> wrapping the input plus text — that is fine for a
            checkbox, but the copy has to say what the flag DOES, since
            "demo" alone doesn't imply "vanishes from the queues". */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={isDemo} style={{ marginTop: 3 }}
              onChange={e => setIsDemo(e.target.checked)} />
            <span>
              מסעדת דמו
              <span style={{ display: 'block', fontWeight: 400, fontSize: '0.8rem', color: '#888', marginTop: 2 }}>
                לא נספרת במבט על ולא מופיעה בלטיפול — נתוני הדגמה, לא עבודה אמיתית
              </span>
            </span>
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success"
            disabled={saving || !name.trim() || !!businessIdError}>
            {saving
              ? (editing ? 'שומר...' : 'יוצר...')
              : (editing
                  ? <><Icon name="save" size={15} /> שמור</>
                  : <><Icon name="plus" size={15} /> צור מסעדה</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Create/edit a network (chain). A network is a name + a set of member
// restaurants, used for consolidated reporting on the analysis page.
export function NetworkModal({ network, restaurants, onSave, onClose }) {
  const editing = !!network;
  const [name, setName] = useState(network?.name || '');
  const [ids, setIds] = useState(
    Array.isArray(network?.restaurant_ids) ? network.restaurant_ids.map(String) : []
  );
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const s = String(id);
    setIds(cur => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ids.length === 0) { toast('יש לבחור לפחות מסעדה אחת', 'error'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), restaurant_ids: ids.map(Number) };
      if (editing) { await adminApi.updateNetwork(network.id, payload); toast('הרשת עודכנה'); }
      else         { await adminApi.createNetwork(payload);            toast('הרשת נוצרה'); }
      onSave();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size={420}
      closeOnBackdrop={false}
      ariaLabel={editing ? 'עריכת רשת' : 'רשת חדשה'}
    >
      <h2 className="modal-title">{editing ? 'עריכת רשת' : 'רשת חדשה'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>שם הרשת *</label>
          <input className="form-control" value={name} autoFocus
            onChange={e => setName(e.target.value)} required placeholder="שם הרשת" />
        </div>
        <div className="form-group">
          <label>מסעדות ברשת * (אפשר לבחור יותר מאחת)</label>
          <div style={{
            border: '1px solid #d4dae5', borderRadius: 6, padding: '8px 10px',
            maxHeight: 200, overflowY: 'auto', background: '#fafbfc',
          }}>
            {restaurants.length === 0 ? (
              <div style={{ color: '#888', fontSize: '0.85rem' }}>אין מסעדות</div>
            ) : (
              restaurants.map(r => (
                <label key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={ids.includes(String(r.id))}
                    onChange={() => toggle(r.id)} />
                  <span>{r.name}</span>
                </label>
              ))
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success" disabled={saving || !name.trim() || ids.length === 0}>
            {saving
              ? 'שומר...'
              : (editing
                  ? <><Icon name="save" size={15} /> שמור</>
                  : <><Icon name="plus" size={15} /> צור רשת</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
